import api from './api';
import endpoints from './endpoints';
import { getCurrentUser } from './authService';

/**
 * 社交链接服务
 */
const socialLinkService = {
  /**
   * 创建社交链接
   * @param {Object} socialLinkData - 社交链接数据
   * @returns {Promise} - 创建结果
   */
  createSocialLink: async (socialLinkData) => {
    try {
      // 获取当前用户ID
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('用户未登录或无法获取用户ID');
      }

      const userId = currentUser.id;
      const endpoint = endpoints.users.socialLinks.create(userId);

      console.log('创建社交链接，用户ID:', userId, '数据:', socialLinkData);

      const response = await api.post(endpoint, socialLinkData);

      // 处理API响应格式
      if (response && response.success && response.data) {
        console.log('社交链接创建成功:', response.data);
        return response.data;
      }

      console.log('社交链接创建成功:', response);
      return response;
    } catch (error) {
      console.error('创建社交链接失败:', error);
      throw error;
    }
  },

  /**
   * 获取用户的社交链接列表
   * @param {string} userId - 用户ID
   * @returns {Promise} - 社交链接列表
   */
  getUserSocialLinks: async (userId) => {
    try {
      const endpoint = endpoints.users.socialLinks.list(userId);
      const response = await api.get(endpoint);

      // 处理API响应格式
      if (
        response &&
        response.success &&
        response.data &&
        response.data.social_links
      ) {
        return response.data.social_links;
      }

      // 兼容直接返回数组的情况
      if (Array.isArray(response)) {
        return response;
      }

      // 兼容其他响应格式
      if (response && response.social_links) {
        return response.social_links;
      }

      return [];
    } catch (error) {
      console.error('获取用户社交链接失败:', error);
      throw error;
    }
  },

  /**
   * 删除社交链接
   * @param {string} type - 社交链接类型
   * @param {string} display_name - 社交链接显示名称（仅自定义类型需要）
   * @returns {Promise} - 删除结果
   */
  deleteSocialLink: async (type, display_name) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('用户未登录或无法获取用户ID');
      }

      const userId = currentUser.id;
      let endpoint = endpoints.users.socialLinks.delete(userId, type);

      // 如果是自定义类型，需要在URL中添加display_name查询参数
      if (type === 'custom' && display_name) {
        endpoint += `?display_name=${encodeURIComponent(display_name)}`;
      }

      const response = await api.delete(endpoint);

      // 处理API响应格式
      if (response && response.success && response.data) {
        return response.data;
      }

      return response;
    } catch (error) {
      console.error('删除社交链接失败:', error);
      throw error;
    }
  },

  /**
   * 更新社交链接
   * @param {string} type - 社交链接类型
   * @param {Object} updateData - 更新数据
   * @returns {Promise} - 更新结果
   */
  updateSocialLink: async (type, updateData) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('用户未登录或无法获取用户ID');
      }

      const userId = currentUser.id;
      const endpoint = endpoints.users.socialLinks.update(userId, type);

      const response = await api.put(endpoint, updateData);

      // 处理API响应格式
      if (response && response.success && response.data) {
        return response.data;
      }

      return response;
    } catch (error) {
      console.error('更新社交链接失败:', error);
      throw error;
    }
  },

  /**
   * 批量更新社交链接（完整替换）
   * @param {Array} socialLinks - 社交链接数组
   * @returns {Promise} - 更新结果
   */
  batchUpdateSocialLinks: async (socialLinks) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('用户未登录或无法获取用户ID');
      }

      const userId = currentUser.id;
      const endpoint = endpoints.users.socialLinks.batchUpdate(userId);

      const requestData = {
        social_links: socialLinks,
      };

      const response = await api.put(endpoint, requestData);

      // 处理API响应格式
      if (
        response &&
        response.success &&
        response.data &&
        response.data.social_links
      ) {
        return response.data.social_links;
      }

      return response;
    } catch (error) {
      console.error('批量更新社交链接失败:', error);
      throw error;
    }
  },
};

export default socialLinkService;
